import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSingleCustomCollection from '@/hooks/useSingleCustomCollection'
import { useNotifications } from '@/hooks/useNotifications'

// Components
import SingleCustomCollectionTable from './components/SingleCustomCollectionTable'

// Hooks
import { useItemSelection } from './hooks/useItemSelection'

// Widgets
import LoadMoreSection from '@/widgets/LoadMoreSection'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import SearchActionsBar from '@/widgets/SearchActionsBar'

export default function SingleCustomCollectionList({ collectionId }) {
	const { t } = useTranslation()
	const navigate = useNavigate()

	// State for search and filters
	const [searchTerm, setSearchTerm] = useState('')
	const [lookupOptions, setLookupOptions] = useState({})

	// Item selection hook
	const { selectedItems, handleSelectItem, handleSelectAll, clearSelection, getSelectionState } = useItemSelection()

	const {
		items,
		collectionName,
		collectionDetails,
		loading,
		loadingCollection,
		loadingMore,
		deleting,
		error,
		message,
		totalCount,
		hasMore,
		filters,
		sortColumn,
		sortDirection,
		initializeCollection,
		loadItems,
		handleLoadMore,
		deleteItem,
		setSorting,
		updateFilters,
		setFilters,
		clearError,
		clearMessage,
	} = useSingleCustomCollection(collectionId)

	// Notifications hook
	const { showDeleteConfirmation, showDeleteSuccess } = useNotifications({ error, message, clearError, clearMessage })

	// Initialize the collection when collectionId is available
	useEffect(() => {
		if (collectionId) {
			initializeCollection()
		}
	}, [collectionId, initializeCollection])

	// Load items after collection is initialized
	useEffect(() => {
		if (collectionName) {
			loadItems()
		}
	}, [collectionName, loadItems])

	// Handle search with debouncing
	useEffect(() => {
		// console.log("TCL -> CustomCollections -> updateFilters:", updateFilters)
		const timer = setTimeout(() => {
			const v = (searchTerm || '').trim()
			if (v.length === 0 || v.length >= 3) {
				updateFilters({ searchText: v })
			}
		}, 200)

		return () => clearTimeout(timer)
	}, [searchTerm, updateFilters])

	// Handle search
	const handleSearchChange = e => {
		setSearchTerm(e?.target ? e.target.value : e)
	}

	// Handle column filter updates
	const handleColumnFilter = (columnName, filterValue) => {
		// Get current filters and create new object
		const currentFilters = { ...filters }

		if (filterValue === null) {
			// Remove filter completely
			delete currentFilters[columnName]
		} else {
			// Add/update filter
			currentFilters[columnName] = filterValue
		}

		// Replace all filters using setFilters
		setFilters(currentFilters)
		// console.log("TCL -> handleColumnFilter -> currentFilters:", currentFilters)
	}

	// Handle delete selected
	const handleDeleteSelected = async () => {
		if (selectedItems.size === 0) return

		const confirmed = await showDeleteConfirmation(
			selectedItems.size,
			t('singleCustomCollection.list.deleteDialog.confirmText', { count: selectedItems.size })
		)

		if (confirmed) {
			const deletePromises = Array.from(selectedItems).map(itemId => deleteItem(itemId))

			try {
				await Promise.all(deletePromises)
				const deletedCount = selectedItems.size
				clearSelection()
				showDeleteSuccess(
					deletedCount,
					t('singleCustomCollection.list.deleteDialog.deletedSuccess', { count: deletedCount })
				)
			} catch (error) {
				console.error('Error deleting items:', error)
			}
		}
	}

	// Handle column sorting
	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	// Handle navigation to add page
	const handleAddNew = () => {
		navigate(`/single-custom-collection/${collectionId}/add`)
	}

	// Wrapper for select all to match component interface
	const onSelectAll = checked => {
		handleSelectAll(checked, items)
	}

	// Get selection state
	const { isAllSelected, isIndeterminate } = getSelectionState(items)

	// Show loading indicator while loading collection details
	if (loadingCollection) {
		return <LoadingIndicator message={t('singleCustomCollection.list.loading')} />
	}

	// Show error if collection not found
	if (!collectionName && !loadingCollection) {
		return (
			<div className="text-center py-12 px-4 text-gray-500 bg-white rounded-lg">
				<h2 className="text-xl font-semibold text-gray-900 mb-2">{t('singleCustomCollection.list.notFound.title')}</h2>
				<p className="text-gray-600">{t('singleCustomCollection.list.notFound.description')}</p>
				<button
					onClick={() => navigate('/system/custom-collections')}
					className="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
					{t('singleCustomCollection.list.notFound.backButton')}
				</button>
			</div>
		)
	}

	const sortFieldLabel =
		collectionDetails?.sortField === 'created' || !collectionDetails?.sortField
			? t('singleCustomCollection.list.summary.createdField')
			: collectionDetails.sortField

	const sortDirectionLabel = t(`customCollections.list.sortDirections.${collectionDetails?.sortDirection || 'desc'}`)

	const buttons = [
		{ type: 'delete', label: t('singleCustomCollection.list.actions.delete'), count: selectedItems.size, onClick: handleDeleteSelected, loading: deleting },
		{ type: 'add', label: t('singleCustomCollection.list.actions.addNew'), onClick: handleAddNew },
	]

	return (
		<>
			<div className="mb-6 bg-white rounded-lg shadow p-6">
				<div className="flex justify-between items-start">
					<div>
						<h1 className="text-2xl font-bold text-gray-900">{collectionName}</h1>
						{collectionDetails?.description && <p className="text-gray-600 mt-2">{collectionDetails.description}</p>}
						<div className="mt-2 text-sm text-gray-500">
							<span>{t('singleCustomCollection.list.summary.totalItems', { count: totalCount })}</span>
							<span className="ml-3">
								{t('singleCustomCollection.list.summary.defaultSort', {
									field: sortFieldLabel,
									direction: sortDirectionLabel,
								})}
							</span>
						</div>
					</div>
				</div>
			</div>

			<SearchActionsBar
				value={searchTerm}
				onChange={handleSearchChange}
				placeholder={t('singleCustomCollection.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<SingleCustomCollectionTable
				items={items}
				collectionDetails={collectionDetails}
				loading={loading}
				selectedItems={selectedItems}
				onSelectAll={onSelectAll}
				onSelectItem={handleSelectItem}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				collectionId={collectionId}
				filters={filters}
				onApplyFilter={handleColumnFilter}
				lookupOptions={lookupOptions}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={items.length}
				totalCount={totalCount}
			/>
		</>
	)
}
